import { ReactNode, useCallback, useState } from 'react';

import 'react-international-phone/style.css';

import { normalizeToE164 } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  getCountry,
  ParsedCountry,
  PhoneInputProps as ReactInternationalPhoneInputProps,
  usePhoneInput,
} from 'react-international-phone';

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import ArrowDownIcon from '../icons/arrow-down';
import ArrowUpIcon from '../icons/arrow-up';
import CheckIcon from '../icons/check';
import { ScrollArea } from './scroll-area';

type PhoneInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> &
  Omit<ReactInternationalPhoneInputProps, 'onChange'> & {
    onChange?: (value: string) => void;
    error?: boolean;
    onCountryChange?: (country: CountryIso2) => void;
    placeholder?: string;
    userCountry?: CountryIso2;
    suffix?: ReactNode;
    inputClassName?: string;
    countrySelectTriggerClassName?: string;
  };

function PhoneInput({
  className,
  onChange,
  error,
  onCountryChange,
  placeholder,
  userCountry,
  suffix,
  disabled,
  inputClassName,
  countrySelectTriggerClassName,
  ...props
}: PhoneInputProps) {
  const handlePhoneInput = ({ phone }: { phone: string; inputValue: string }) => {
    onChange?.(normalizeToE164(phone, country.iso2) ?? phone);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedValue = event.clipboardData.getData('Text');
    onChange?.(normalizeToE164(pastedValue, country.iso2) ?? pastedValue);
  };

  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    onChange: ({ phone }) => handlePhoneInput({ phone, inputValue }),
    defaultCountry: userCountry,
    disableDialCodeAndPrefix: true,
    countries: [
      ...defaultCountries.filter((c) => c[1] !== 'au'),
      [
        'Australia',
        'au',
        '61',
        {
          default: '. .... ....',
          '/^0?4/': '... ... ....',
          '/^0?5(?!50)/': '... ... ....',
          '/^1(3|8)00/': '.... ... ...',
          '/^13/': '.. .. ..',
          '/^180/': '... ....',
        },
        0,
        [],
      ],
    ],
    ...props,
  });

  return (
    <div className={cn('flex items-stretch', className)}>
      <CountrySelect
        onChange={(countryIso) => {
          setCountry(countryIso);
          onCountryChange?.(countryIso);
        }}
        value={country}
        options={defaultCountries.map((c) => ({
          label: getCountry({ field: 'name', value: c[0] })?.name || '',
          value: c[1],
        }))}
        disabled={disabled}
        triggerClassName={countrySelectTriggerClassName}
      />
      <Input
        ref={inputRef}
        error={error}
        className={cn('w-full flex-1 rounded-s-none rounded-e', inputClassName)}
        value={inputValue}
        onChange={handlePhoneValueChange}
        onPaste={handlePaste}
        inputMode="numeric"
        containerClassName="flex-1"
        placeholder={placeholder}
        disabled={disabled}
        suffix={suffix}
      />
    </div>
  );
}
PhoneInput.displayName = 'PhoneInput';

type CountrySelectOption = { label: string; value: CountryIso2 };

type CountrySelectProps = {
  disabled?: boolean;
  value: ParsedCountry;
  onChange: (
    countryIso2: CountryIso2,
    options?: {
      focusOnInput: boolean;
    },
  ) => void;
  options: CountrySelectOption[];
  triggerClassName?: string;
};

const CountrySelect = ({ disabled, value, onChange, options, triggerClassName }: CountrySelectProps) => {
  const [open, setOpen] = useState(false);
  const handleSelect = useCallback(
    (country: CountryIso2) => {
      onChange(country);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          tabIndex={-1}
          type="button"
          variant={'outline'}
          className={cn('bg-grey-1 flex h-auto gap-1 rounded-s rounded-e-none border-2 border-r-0 px-3 py-0 shadow-sm', triggerClassName)}
          disabled={disabled}>
          <FlagComponent country={value.iso2} />
          {value.dialCode && <p>(+{value.dialCode})</p>}
          {!open && <ArrowDownIcon className={cn('-mr-1 h-3.5 w-3.5 opacity-50', disabled ? 'hidden' : 'opacity-100')} />}
          {open && <ArrowUpIcon className={cn('-mr-1 h-3.5 w-3.5 opacity-50', disabled ? 'hidden' : 'opacity-100')} />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandList>
            <ScrollArea className="h-72">
              <CommandInput placeholder="Search country..." />
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {options
                  .filter((x) => x.value)
                  .map((option) => (
                    <CommandItem className="gap-2" key={option.value} onSelect={() => handleSelect(option.value)}>
                      <FlagComponent country={option.value} />
                      <span className="flex-1 text-sm">{option.label}</span>
                      {option.value && (
                        <span className="text-foreground/50 text-sm">
                          {`+${
                            getCountry({
                              field: 'iso2',
                              value: option.value,
                            })?.dialCode
                          }`}
                        </span>
                      )}
                      <CheckIcon className={cn('ml-auto h-4 w-4', option.value === value.iso2 ? 'opacity-100' : 'opacity-0')} />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const FlagComponent = ({ country }: { country: CountryIso2 }) => {
  return (
    <span className="flex w-4 flex-col justify-center overflow-hidden rounded-sm">
      <FlagImage iso2={country} size="16px" />
    </span>
  );
};
FlagComponent.displayName = 'FlagComponent';

export { PhoneInput };
